/**
 * Los dos bloques de empleados digitales que no producen. **Son dos, y no se
 * juntan.**
 *
 *  - **Instalados sin uso** (`variante="sin-uso"`): están desplegados, se los
 *    midió y dieron **cero trazas**. Es el bloque que cierra la cadena
 *    contratado → instalado → activo y el corazón del módulo: hoy figuran como
 *    éxito en la brecha de activación de Clientes.
 *  - **Sin medición** (`variante="sin-medicion"`): están desplegados y **nadie
 *    los pudo medir**, porque la plataforma no les estampó el prefijo de sesión
 *    de Langfuse. Su `consumo` es `null`, no `{trazas: 0}`. Decir "cero trazas"
 *    de un agente que nadie midió sería afirmar algo que los datos no sostienen,
 *    y la columna de trazas de este bloque dice "Sin medición" en palabras.
 *
 * Cada fila abre su ficha en la plataforma (el sistema que sabe qué agente es de
 * qué cuenta) y sus sesiones en Langfuse (el sistema que mide). El segundo hoy
 * no existe y se renderiza con su motivo — ver `LinkLangfuse`.
 *
 * **El seguimiento se abre sobre la cuenta, no sobre el agente.** `EntityType`
 * del contrato tiene tres valores (`client`, `metric`, `activation`) y ninguno
 * es "agente": crear el work item contra un id que ninguna entidad del panel
 * declara dejaría un seguimiento que después nadie puede volver a encontrar
 * desde la ficha. El título y el contexto nombran al agente, que es lo que hace
 * falta para trabajarlo, y la cuenta es donde queda colgado.
 */
import type { Agent, Links } from '@ops/contracts'
import { AGENT_ESTADO_DESCRIPCIONES, AGENT_ESTADO_LABELS } from '@ops/contracts'

import { formatMetricValue, plural } from '../../lib/format'
import { useVincular } from '../../lib/vincular'
import {
  Button,
  DataTable,
  ExternalLink,
  MissingCell,
  Panel,
  StatusPill,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import { severidadDeAgente, severidadSinUso } from './derivar'
import { LinkLangfuse } from './LinkLangfuse'

export type VarianteAgentes = 'sin-uso' | 'sin-medicion'

export interface AgentesBlockProps {
  variante: VarianteAgentes
  agentes: readonly Agent[]
  links: Links
  /** Abre el diálogo de seguimiento sobre la cuenta del agente. */
  onCrearSeguimiento: (agente: Agent) => void
  id?: string
}

const TITULO: Record<VarianteAgentes, string> = {
  'sin-uso': 'Empleados digitales instalados que no producen',
  'sin-medicion': 'Empleados digitales que no se pueden medir',
}

const SUBTITULO: Record<VarianteAgentes, string> = {
  'sin-uso':
    'Desplegados y con cero trazas en el período. Están instalados, así que la brecha de activación los cuenta como éxito.',
  'sin-medicion':
    'Desplegados y sin prefijo de sesión en la plataforma: no se puede saber qué sesiones de Langfuse son suyas. Su consumo es desconocido, no es cero.',
}

const VACIO: Record<VarianteAgentes, string> = {
  'sin-uso':
    'Todos los empleados digitales instalados tuvieron al menos una traza en el período.',
  'sin-medicion':
    'Todos los empleados digitales instalados tienen su prefijo de sesión estampado: el consumo de cada uno se puede medir.',
}

export function AgentesBlock({
  variante,
  agentes,
  links,
  onCrearSeguimiento,
  id,
}: AgentesBlockProps) {
  const vincular = useVincular()
  const esSinUso = variante === 'sin-uso'

  const columnas: ReadonlyArray<Column<Agent>> = [
    {
      key: 'agente',
      header: 'Empleado digital',
      rowHeader: true,
      width: '250px',
      sortValue: (a) => a.nombre,
      filterValue: (a) => `${a.nombre} ${a.rolFuncional} ${a.clientNombre}`,
      render: (a) => {
        const sobre = links.agentes[a.id]
        return (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate">
              {a.nombre}{' '}
              <span className="font-normal text-muted-strong">
                · {a.rolFuncional}
              </span>
            </span>
            <ExternalLink
              link={sobre?.plataforma ?? null}
              variante="texto"
              sistemaFaltante="la plataforma"
              onVincular={() =>
                vincular(`el agente ${a.nombre} de ${a.clientNombre} en la plataforma`)
              }
            >
              Ver la ficha del agente
            </ExternalLink>
          </span>
        )
      },
    },
    {
      key: 'cuenta',
      header: 'Cuenta',
      width: '170px',
      sortValue: (a) => a.clientNombre,
      render: (a) => a.clientNombre,
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '150px',
      sortValue: (a) => AGENT_ESTADO_LABELS[a.estado],
      render: (a) => (
        /* El color nunca solo: la pill lleva el rótulo y el título dice qué
           significa ese estado en una oración. */
        <StatusPill
          severidad={severidadDeAgente(a)}
          size="sm"
          testId="agente-estado"
        >
          {AGENT_ESTADO_LABELS[a.estado]}
        </StatusPill>
      ),
      filterValue: (a) =>
        `${AGENT_ESTADO_LABELS[a.estado]} ${AGENT_ESTADO_DESCRIPCIONES[a.estado]}`,
    },
    {
      key: 'dias',
      header: 'Días sin actividad',
      align: 'right',
      width: '150px',
      sortValue: (a) => a.diasSinUso,
      render: (a) =>
        a.diasSinUso === null ? (
          /* Sin fecha de despliegue ni de última actividad no hay antigüedad:
             un 0 diría que se desplegó hoy. */
          <MissingCell que="fecha de despliegue" />
        ) : (
          <span className="tabular">
            {formatMetricValue(a.diasSinUso, 'count')}
          </span>
        ),
    },
    {
      key: 'trazas',
      header: 'Trazas del período',
      align: 'right',
      width: '170px',
      sortValue: (a) => a.consumo?.trazas ?? null,
      render: (a) =>
        a.consumo === null ? (
          <span className="inline-flex items-center gap-1.5 text-micro text-muted-strong">
            Sin medición
          </span>
        ) : (
          <span className="tabular">
            {formatMetricValue(a.consumo.trazas, 'count')}
          </span>
        ),
    },
    {
      key: 'langfuse',
      header: 'Consumo en Langfuse',
      width: '270px',
      render: (a) => (
        <LinkLangfuse sobre={links.agentes[a.id]} texto="Sus sesiones" />
      ),
    },
    {
      key: 'accion',
      header: 'Seguimiento',
      width: '160px',
      render: (a) => (
        <Button
          variante="chico"
          icono="mas"
          onClick={() => onCrearSeguimiento(a)}
        >
          Asignar seguimiento
          <span className="sr-only">
            {' '}
            de {a.nombre} en {a.clientNombre}
          </span>
        </Button>
      ),
    },
  ]

  if (agentes.length === 0) {
    return (
      <Panel titulo={TITULO[variante]} subtitulo={SUBTITULO[variante]} padding="sm">
        <Vacio icono={null} mensaje={VACIO[variante]} />
      </Panel>
    )
  }

  return (
    <div id={id} className="min-w-0">
      <Panel
        titulo={TITULO[variante]}
        subtitulo={SUBTITULO[variante]}
        severidad={esSinUso ? 'critical' : 'warning'}
        padding="none"
        footer={
          esSinUso ? (
            <>
              <b>{formatMetricValue(agentes.length, 'count')}</b>{' '}
              {plural(
                agentes.length,
                'empleado digital instalado y con cero trazas',
                'empleados digitales instalados y con cero trazas',
              )}
              . La cuenta paga por ellos y no producen: cada uno es una
              conversación de delivery, no un número de dashboard.
            </>
          ) : (
            <>
              El cruce agente ↔ consumo se hace por el prefijo de sesión que
              estampa la plataforma. Sin él no hay medición posible, y arreglarlo
              no es una tarea de Admin: es trabajo de plataforma.
            </>
          )
        }
      >
        <DataTable
          columns={columnas}
          rows={agentes}
          rowKey={(a) => a.id}
          rowSeverity={(a) =>
            esSinUso ? severidadSinUso(a.diasSinUso) : 'unknown'
          }
          densidadPorSeveridad={esSinUso}
          ordenInicial={esSinUso ? { key: 'dias', dir: 'desc' } : undefined}
          filtroPlaceholder="agente, rol o cuenta"
          emptyMessage={VACIO[variante]}
          caption={
            esSinUso ? (
              <>
                Empleados digitales desplegados con cero trazas medidas en el
                período. El agente sale de la plataforma; las trazas, de Langfuse.
              </>
            ) : (
              <>
                Empleados digitales desplegados sin prefijo de sesión. Su consumo
                figura como desconocido, nunca como cero.
              </>
            )
          }
        />
      </Panel>
    </div>
  )
}
